import React, { useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { Warning } from '@mui/icons-material';
import useAuthStore from './store/useStore';

// Layouts
import MainLayout from './layouts/MainLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Public Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import PropertyListPage from './pages/properties/PropertyListPage';
import PropertyDetailPage from './pages/properties/PropertyDetailPage';

// Protected Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import MyBookingsPage from './pages/dashboard/MyBookingsPage';
import MyPropertiesPage from './pages/dashboard/MyPropertiesPage';
import CreatePropertyPage from './pages/dashboard/CreatePropertyPage';
import TicketsPage from './pages/dashboard/TicketsPage';
import PaymentsPage from './pages/dashboard/PaymentsPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import AIChatPage from './pages/dashboard/AIChatPage';
import RoomPairingPage from './pages/dashboard/RoomPairingPage';
import TenancyPage from './pages/dashboard/TenancyPage';
import EditPropertyPage from './pages/dashboard/EditPropertyPage';
import BrowsePropertiesPage from './pages/dashboard/BrowsePropertiesPage';
import RevenueAnalyticsPage from './pages/dashboard/RevenueAnalyticsPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminKYCPage from './pages/admin/AdminKYCPage';
import AdminPropertiesPage from './pages/admin/AdminPropertiesPage';
import AdminBookingsPage from './pages/admin/AdminBookingsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminTenancyPage from './pages/admin/AdminTenancyPage';
import AdminTicketsPage from './pages/admin/AdminTicketsPage';
import AdminChatManagementPage from './pages/admin/AdminChatManagementPage';
import AdminSystemSettingsPage from './pages/admin/AdminSystemSettingsPage';
import AdminSMSSettingsPage from './pages/admin/AdminSMSSettingsPage';
import AdminAISettingsPage from './pages/admin/AdminAISettingsPage';
import AdminRoomPairingPage from './pages/admin/AdminRoomPairingPage';
import ChatPage from './pages/dashboard/ChatPage';
import DisputesPage from './pages/dashboard/DisputesPage';
import AdminDisputesPage from './pages/admin/AdminDisputesPage';
import KYCGatePage from './pages/dashboard/KYCGatePage';

function ProtectedRoute({ children, roles }) {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/dashboard" />;
  return children;
}

function KYCProtectedRoute({ children }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Super admins and rent_control bypass KYC requirement
  if (user?.role === 'super_admin' || user?.role === 'rent_control') {
    return children;
  }

  // If user is not KYC verified, show the KYC gate page
  if (!user?.is_verified) {
    return <KYCGatePage />;
  }

  return children;
}

function InactivityWarningDialog() {
  const { showInactivityWarning, dismissInactivityWarning, logout } = useAuthStore();
  return (
    <Dialog open={showInactivityWarning} onClose={dismissInactivityWarning} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning color="warning" /> Session Expiring
      </DialogTitle>
      <DialogContent>
        <Typography>
          You've been inactive for a while. Your session will expire in <strong>2 minutes</strong> for security reasons.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Click "Stay Logged In" to continue your session.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => logout('inactivity')} color="inherit">Log Out Now</Button>
        <Button onClick={dismissInactivityWarning} variant="contained" color="primary">Stay Logged In</Button>
      </DialogActions>
    </Dialog>
  );
}

function App() {
  const { checkAuth, isAuthenticated, resetInactivityTimer } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Reset inactivity timer on user interaction
  const handleActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handleActivity));
  }, [isAuthenticated, handleActivity]);

  return (
    <>
    <InactivityWarningDialog />
    <Routes>
      {/* Public Routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/properties" element={<PropertyListPage />} />
        <Route path="/properties/:id" element={<PropertyDetailPage />} />
      </Route>

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Protected Dashboard Routes - shared by all roles (KYC gated) */}
      <Route element={<ProtectedRoute><KYCProtectedRoute><DashboardLayout /></KYCProtectedRoute></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/browse" element={<BrowsePropertiesPage />} />
        <Route path="/dashboard/bookings" element={<MyBookingsPage />} />
        <Route path="/dashboard/tenancy" element={<TenancyPage />} />
        <Route path="/dashboard/tickets" element={<TicketsPage />} />
        <Route path="/dashboard/payments" element={<PaymentsPage />} />
        <Route path="/dashboard/profile" element={<ProfilePage />} />
        <Route path="/dashboard/ai-assistant" element={<AIChatPage />} />
        <Route path="/dashboard/chat" element={<ChatPage />} />
        <Route path="/dashboard/disputes" element={<DisputesPage />} />
      </Route>

      {/* Landlord/Agent-only Routes (KYC gated) */}
      <Route element={<ProtectedRoute roles={['landlord', 'agent', 'super_admin']}><KYCProtectedRoute><DashboardLayout /></KYCProtectedRoute></ProtectedRoute>}>
        <Route path="/dashboard/properties" element={<MyPropertiesPage />} />
        <Route path="/dashboard/properties/create" element={<CreatePropertyPage />} />
        <Route path="/dashboard/properties/:id/edit" element={<EditPropertyPage />} />
        <Route path="/dashboard/revenue" element={<RevenueAnalyticsPage />} />
      </Route>

      {/* Room Pairing - All authenticated users (KYC gated) */}
      <Route element={<ProtectedRoute><KYCProtectedRoute><DashboardLayout /></KYCProtectedRoute></ProtectedRoute>}>
        <Route path="/dashboard/room-pairing" element={<RoomPairingPage />} />
      </Route>

      {/* Admin Routes */}
      <Route element={<ProtectedRoute roles={['super_admin', 'rent_control']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/kyc" element={<AdminKYCPage />} />
        <Route path="/admin/properties" element={<AdminPropertiesPage />} />
        <Route path="/admin/bookings" element={<AdminBookingsPage />} />
        <Route path="/admin/payments" element={<AdminPaymentsPage />} />
        <Route path="/admin/tenancy" element={<AdminTenancyPage />} />
        <Route path="/admin/tickets" element={<AdminTicketsPage />} />
        <Route path="/admin/room-pairing" element={<AdminRoomPairingPage />} />
        <Route path="/admin/chat" element={<AdminChatManagementPage />} />
        <Route path="/admin/settings/system" element={<AdminSystemSettingsPage />} />
        <Route path="/admin/settings/sms" element={<AdminSMSSettingsPage />} />
        <Route path="/admin/settings/ai" element={<AdminAISettingsPage />} />
        <Route path="/admin/disputes" element={<AdminDisputesPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </>
  );
}

export default App;
